import {
  StorageLocationId,
  storageLocationIdFormat,
} from "@redotech/redo-warehouse-id";

export interface ProductBinInfo {
  productId: string;
  productName: string;
  imageUrl?: string | null;
  requiredQuantity: number;
  bins: {
    storageLocationId: string;
    binName: string;
    availableQuantity: number;
    inventoryItemId?: string | null;
    lotNumber?: string | null;
    expirationDate?: string | null;
  }[];
}

export interface BinSelection {
  storageLocationId: StorageLocationId;
  quantity: number;
  inventoryItemId?: string | null;
}

export type BinSelections = Record<string, BinSelection[]>;

export interface ApplyAllBinOption {
  id: string;
  value: string;
  type: "text";
  text: string;
  supportingText: string;
}

export function computeDefaultAllocations(
  product: ProductBinInfo,
): Map<string, number> {
  const allocations = new Map<string, number>();
  let remaining = product.requiredQuantity;

  for (const bin of product.bins) {
    if (remaining <= 0) {
      break;
    }
    const qty = Math.min(remaining, bin.availableQuantity);
    allocations.set(bin.storageLocationId, qty);
    remaining -= qty;
  }

  return allocations;
}

export function applyBinToAllProducts(
  lineItems: ProductBinInfo[],
  allocations: Map<string, Map<string, number>>,
  visibleBins: Map<string, Set<string>>,
  storageLocationId: string,
): {
  allocations: Map<string, Map<string, number>>;
  visibleBins: Map<string, Set<string>>;
  appliedProductIds: string[];
} {
  const nextAllocations = new Map(allocations);
  const nextVisible = new Map(visibleBins);
  const appliedProductIds: string[] = [];
  for (const item of lineItems) {
    const bin = item.bins.find(
      (b) => b.storageLocationId === storageLocationId,
    );
    if (!bin || bin.availableQuantity < item.requiredQuantity) {
      continue;
    }
    nextAllocations.set(
      item.productId,
      new Map([[storageLocationId, item.requiredQuantity]]),
    );
    nextVisible.set(item.productId, new Set([storageLocationId]));
    appliedProductIds.push(item.productId);
  }
  return {
    allocations: nextAllocations,
    visibleBins: nextVisible,
    appliedProductIds,
  };
}

export function buildApplyAllOptions(
  lineItems: ProductBinInfo[],
): ApplyAllBinOption[] {
  const productCountByBin = new Map<
    string,
    { binName: string; productCount: number; fullyStocked: number }
  >();
  for (const item of lineItems) {
    const seen = new Set<string>();
    for (const bin of item.bins) {
      if (seen.has(bin.storageLocationId)) {
        continue;
      }
      seen.add(bin.storageLocationId);
      const entry = productCountByBin.get(bin.storageLocationId) ?? {
        binName: bin.binName,
        productCount: 0,
        fullyStocked: 0,
      };
      entry.productCount += 1;
      if (bin.availableQuantity >= item.requiredQuantity) {
        entry.fullyStocked += 1;
      }
      productCountByBin.set(bin.storageLocationId, entry);
    }
  }
  return [...productCountByBin.entries()]
    .filter(([, entry]) => entry.productCount > 1)
    .sort((a, b) => b[1].fullyStocked - a[1].fullyStocked)
    .map(([storageLocationId, entry]) => ({
      id: storageLocationId,
      value: storageLocationId,
      type: "text" as const,
      text: entry.binName,
      supportingText: `Enough stock for ${entry.fullyStocked} of ${lineItems.length} products`,
    }));
}

export function buildBinSelections(
  lineItems: ProductBinInfo[],
  allocations: Map<string, Map<string, number>>,
): BinSelections {
  const binSelections: BinSelections = {};
  const itemById = new Map(lineItems.map((item) => [item.productId, item]));

  for (const [productId, productAllocs] of allocations.entries()) {
    const item = itemById.get(productId);
    const inventoryItemByBin = new Map<string, string | null>();
    for (const bin of item?.bins ?? []) {
      if (!inventoryItemByBin.has(bin.storageLocationId)) {
        inventoryItemByBin.set(
          bin.storageLocationId,
          bin.inventoryItemId ?? null,
        );
      }
    }

    const selections: BinSelection[] = [];
    for (const [storageLocationId, quantity] of productAllocs.entries()) {
      if (quantity > 0) {
        selections.push({
          storageLocationId:
            storageLocationIdFormat.schema.parse(storageLocationId),
          quantity,
          inventoryItemId:
            inventoryItemByBin.get(storageLocationId) ?? undefined,
        });
      }
    }
    if (selections.length > 0) {
      binSelections[productId] = selections;
    }
  }
  return binSelections;
}
